<template>
    <v-dialog v-model="dialog" width="1200" scrollable :fullscreen="isMobileAndTablet" style="z-index:20001;">
        <v-card min-height="80vh">
            <div class="primary white--text">
                <v-row>
                    <v-col sm="5">
                        <v-row>
                            <v-col cols="auto" class="pr-0">
                                <v-icon size="40px" class="ml-3" color="white">
                                    mdi-{{ premiumInfo.icon }}
                                </v-icon>
                            </v-col>
                            <v-col>
                                <h3 class="word-break">
                                    {{ insightName }}
                                    <v-icon v-if="isMobilePort && premiumInfo.description" small color="white"
                                        class="ml-1" @click="expand = !expand">
                                        mdi-information
                                    </v-icon>
                                </h3>
                                <p class="mb-0 caption word-break font-weight-bold">{{ title }}</p>
                                <p v-if="sectionTitle" class="mb-0 caption word-break">{{ sectionTitle }}</p>
                            </v-col>
                        </v-row>
                    </v-col>

                    <template v-if="!isMobilePort">
                        <v-col v-if="premiumInfo.description" :cols="isMobilePort && '12'"
                            :order="isMobilePort ? '2' : undefined" class="pt-0" align-self="center">
                            <p class="mb-0 mr-4 caption word-break mt-3" :class="{ 'mx-2': isMobilePort }">
                                {{ premiumInfo.description }}
                                <a v-if="premiumInfo.learnMoreUrl"
                                    class="white--text text-decoration-underline pl-1 fs-14"
                                    :href="premiumInfo.learnMoreUrl" target="_blank">
                                    <b>{{ premiumInfo.learnMoreLabel }}</b>
                                </a>
                            </p>
                        </v-col>
                        <v-spacer v-else />
                    </template>

                    <v-col cols="auto">
                        <v-icon class="white--text mr-4" @click="close">close</v-icon>
                    </v-col>
                </v-row>

                <v-expand-transition v-if="isMobilePort && premiumInfo.description">
                    <div v-show="expand">
                        <v-divider></v-divider>
                        <p class="ma-2 caption">
                            {{ premiumInfo.description }}
                            <a v-if="premiumInfo.learnMoreUrl"
                                class="white--text text-decoration-underline pl-1 fs-14"
                                :href="premiumInfo.learnMoreUrl" target="_blank">
                                <b>{{ premiumInfo.learnMoreLabel }}</b>
                            </a>
                        </p>
                    </div>
                </v-expand-transition>
            </div>

            <loading-linear v-if="isLoading" />

            <template v-else>
                <template v-if="tabs.length">
                    <div v-if="premiumInfo.dropdown" class="grey lighten-3 pa-3">
                        <v-select v-model="tab" outlined dense hide-details :items="tabs" item-value="key"
                            item-text="title" background-color="white" :menu-props="{ bottom: true, offsetY: true }" />
                    </div>

                    <v-tabs v-else v-model="tab" show-arrows color="primary" background-color="grey lighten-3"
                        active-class="tags-history-tab" style="flex: unset;">
                        <v-tab v-for="item in tabs" :key="item.id">{{ item.title }}</v-tab>
                    </v-tabs>

                    <v-tabs-items class="fill-height overflow-auto" v-model="tab">
                        <v-tab-item class="fill-height" v-for="(item, key) in tags" :key="key">
                            <v-card flat height="100%">
                                <v-card-text class="fill-height" :class="{
                                    'pa-0': ['PDF', 'compliance calculators'].includes(getInsightType(item)),
                                    'pa-5': !['PDF', 'compliance calculators'].includes(getInsightType(item))
                                }">
                                    <component :is="getComponentByType(getInsightType(item))" :tag="item"
                                        :tag-data="item" class="text-center" />
                                </v-card-text>
                            </v-card>
                        </v-tab-item>
                    </v-tabs-items>

                    <clear-calcs-footer v-if="displayClearCalcFooter" />
                    <premium-tag-rating v-if="!hasTagRating" :tag="tagId" @submit="onRatingSubmit" />
                </template>

                <div v-else class="pa-5">
                    <h2>No results found...</h2>
                </div>
            </template>
        </v-card>
    </v-dialog>
</template>
<script>
import Mobile from "../../../inc/mixins/mobile";
import { ApiService } from "../../../../api/ApiService";
import { TagInsightApi } from "../../../../api/TagInsightApi";
import { EventNames } from "../../../../classes/EventNames";

import LoadingLinear from "../../../loaders/LoadingLinear.vue";
import TitleContent from "./TitleContent.vue";
import PDFContent from "./PDFContent.vue";
import IFrameContent from "./IFrameContent.vue";
import VideoContent from "./VideoContent.vue";
import LinkContent from "./LinkContent.vue";
import XMLIdContent from "./XMLIdContent.vue";
import PremiumAnswersContent from "./PremiumAnswersContent.vue";

import { ObjectUtils } from "../../../../util/ObjectUtils";
import PremiumTagRating from "./PremiumTagRating.vue";
import ClearCalcsFooter from "./ClearCalcsFooter.vue";

export default {
    components: {
        PremiumAnswersContent,
        XMLIdContent,
        PremiumTagRating,
        TitleContent,
        PDFContent,
        IFrameContent,
        VideoContent,
        LinkContent,
        LoadingLinear,
        ClearCalcsFooter
    },
    mixins: [Mobile],
    data() {
        return {
            expand: false,
            dialog: false,
            premiumContentCategoryInfo: null,
            contentId: null,
            title: null,
            sectionTitle: null,
            insightId: null,
            insightName: '',
            isLoading: true,
            tab: 0,
            tags: [],
            displayClearCalcFooter: false
        };
    },
    computed: {
        tabs() {
            return (this.tags || []).map((value, key) => ({
                key,
                id: value.id,
                title: value.tag.title
            }));
        },
        tagContent() {
            return this.tags[this.tab];
        },
        tagId() {
            return ObjectUtils.dottedGet(this.tagContent, 'tag.id');
        },
        hasTagRating() {
            return this.tagContent && this.tagContent.user_rating !== null;
        },
        premiumInfo() {
            return this.premiumContentCategoryInfo || {};
        }
    },
    methods: {
        getInsightType(item) {
            return item.tag.content_type.name;
        },
        getComponentByType(type) {
            const map = {
                'premium answers': 'premium-answers-content',
                'compliance calculators': 'i-frame-content',
                'PDF': 'p-d-f-content',
                'Link': 'link-content',
                'Video': 'video-content',
                'Text': 'title-content',
                'xml': 'x-m-l-id-content'
            };
            return map[type] || 'div';
        },
        onRatingSubmit(rating) {
            setTimeout(() => {
                this.tags[this.tab]['user_rating'] = rating;
            }, 2000);
        },
        logTagClick(isDefault) {
            const id = this.tagId;
            ApiService.logPremiumTagClick(id, isDefault);
        },
        async loadTagsForCategory() {
            this.isLoading = true;
            try {
                const request = {
                    contentId: this.contentId,
                    chapterId: this.contentId,
                    reportType: 'premiumcontent',
                    reportTypeShow: 'premiumContentShow',
                    codeInsightId: this.insightId
                };

                const { data } = await TagInsightApi.getReportTagInfo(request);
                const rct = data?.reportCategoryTags || {};

                if (this.insightId && rct[this.insightId]) {
                    const categoryData = rct[this.insightId];
                    this.tags = categoryData.category || [];
                    this.displayClearCalcFooter = categoryData?.categoryName?.toLowerCase() === 'compliance calculators';
                    this.logTagClick(true);
                }
            } catch (error) {
                console.error('Error loading tags:', error);
            } finally {
                this.isLoading = false;
            }
        },
        close() {
            this.dialog = false;
        },
        async getPremiumContentCategoryInfo(data) {
            try {
                const request = { categoryId: data.id, type: 'premium' };
                const response = await TagInsightApi.getPremiumInsightInfo(request);
                if (response.data?.data?.length) {
                    this.premiumContentCategoryInfo = response.data.data[0];
                }
            } catch (e) {
                console.error('Failed to load tag category info', e);
            }
        },
        openPremiumDialog(data) {
            this.getPremiumContentCategoryInfo(data);
            this.contentId = data.contentId;
            this.title = data.title;
            this.sectionTitle = data.sectionTitle;
            this.insightName = data.insightName;
            this.insightId = data.id;
            this.dialog = true;
            this.loadTagsForCategory();
        }
    },
    mounted() {
        EventBus.listen(EventNames.INSIGHT_PREMIUM_DIALOG, this.openPremiumDialog);
        this.$watch(() => this.tab, () => this.logTagClick(false));
    },
    beforeDestroy() {
        EventBus.off(EventNames.INSIGHT_PREMIUM_DIALOG, this.openPremiumDialog);
    }
};
</script>

<style lang="scss">
    .tags-history-tab {
        background: unset !important;
        color: unset !important;
    }

    .word-break {
        word-break: break-word !important;
    }
</style>
